import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, IconButton } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import EventBus from '@services/EventBus';
import MailContent from '@components/workflow/messagedialog/MailContent';
import SmsContent from '@components/workflow/messagedialog/SmsContent';

export default function MessageDialog({ initialData }) {
    const { t } = useTranslation();
    
    const messageContentRef = useRef(null);
    const [openDialog, setOpenDialog] = useState(false);
    const [currentElement, setCurrentElement] = useState(null);
    const [currentModeling, setCurrentModeling] = useState(null);
    const [initialContent, setInitialContent] = useState('');
    const [messageType, setMessageType] = useState(null);
    const [isContentValid, setIsContentValid] = useState(true);

    useEffect(() => {
        // Écouter l'événement d'ouverture de la modale
        const handleOpenMessageDialog = (data) => {
            setCurrentElement(data.element);
            setCurrentModeling(data.modeling);
            setMessageType(data.type);
            // Récupérer le contenu JSON existant
            setInitialContent(data.element.businessObject['message-json'] || '');
            setOpenDialog(true);
        };

        EventBus.on('openMessageDialog', handleOpenMessageDialog);

        // Nettoyage
        return () => {
            EventBus.off('openMessageDialog', handleOpenMessageDialog);
        };
    }, [initialData]);

    const handleCloseDialog = (event, reason) => {
        if (reason && reason === 'backdropClick') {
            return; // Ne pas fermer si clic sur le fond
        }
        setOpenDialog(false);
        setCurrentElement(null);
        setCurrentModeling(null);
        setInitialContent('');
    };

    const handleSaveDialog = () => {
        if (currentElement && messageContentRef.current && currentModeling) {
            const content = messageContentRef.current.getContent();
            const jsonContent = JSON.stringify(content, null, 2);
            
            // Émettre un événement pour mettre à jour le JSON
            EventBus.emit('updateMessageJson', {
                element: currentElement,
                modeling: currentModeling,
                json: jsonContent
            });
        }
        handleCloseDialog();
    };

    return (
        <Dialog 
            open={openDialog} 
            onClose={handleCloseDialog}
            maxWidth={messageType === 'sms' ? 'sm' : 'md'}
            fullWidth
            disableEnforceFocus
            disableAutoFocus
            disableRestoreFocus
        >
            <DialogTitle>
                {t('Configure message')} : {messageType === 'sms' ? t('Send a SMS') : t('Send an email')}
            </DialogTitle>
            <IconButton
                aria-label="close"
                onClick={handleCloseDialog}
                sx={{
                    position: 'absolute',
                    right: 8,
                    top: 8,
                }}
            >
                <CloseIcon />
            </IconButton>
            <DialogContent sx={{ pt: 0 }}>
                {messageType === 'sms' ? (
                    <SmsContent 
                        ref={messageContentRef}
                        initialContent={initialContent}
                        onValidityChange={setIsContentValid}
                    />
                ) : (
                    <MailContent 
                        ref={messageContentRef}
                        initialContent={initialContent}
                    />
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={handleCloseDialog}>{t('Cancel')}</Button>
                <Button 
                    variant="contained" 
                    color="primary" 
                    onClick={handleSaveDialog}
                    disabled={messageType === 'sms' && !isContentValid}
                >
                    {t('Validate')}
                </Button>
            </DialogActions>
        </Dialog>
    );
}